import Button from '@/components/UI/Button';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import Icon from '@/components/UI/Icon';
import SearchSelect from '@/components/UI/SearchSelect/SearchSelect';
import type { WebhookCreate, WebhookEventType } from '@/model/WebhookConfiguration.model';
import { _isNotBlank } from '@/littledash';
import { successToast } from '@/helpers';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { RouteService } from '@/support/RouteService';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { FC } from 'react';
import { useCallback } from 'react';
import { FormProvider, useController, useFieldArray, useForm } from 'react-hook-form@latest';
import { FieldArrayError, FormInput } from '../FormInput';
import type { WebhookFormValues } from '../Webhook.utils';
import {
  headerKeyRules,
  headerValueRules,
  nameValidator,
  URL_MAX_LENGTH,
  urlValidator,
  useDebouncedUrlUnique,
  useRegisterHeader,
  useWebhookEvents,
} from '../Webhook.utils';
import styles from '../Webhook.module.scss';

interface AddWebhookProps {
  closeModal: () => void;
  onAddWebhook: () => void;
  eventTypes: Array<WebhookEventType>;
}

/**
 * Modal for creating a new webhook configuration. Collects an HTTPS endpoint URL,
 * at least one authentication header, and event subscriptions.
 */
export const AddWebhook: FC<AddWebhookProps> = ({ onAddWebhook, closeModal, eventTypes }) => {
  const { invoke: createWebhook, loading: isSubmitting } = useApiHook({
    endpoint: 'POST /api/v1/webhooks',
    invokeOnInit: false,
    options: { onError: { toast: true, throw: true, slug: 'create-webhook' } },
  });

  const formMethods = useForm<WebhookFormValues>({
    defaultValues: {
      name: '',
      url: '',
      headers: [{ key: '', value: '' }],
      events: [],
    },
    mode: 'onChange',
  });

  const { register, handleSubmit, setValue, control, formState, getValues, trigger } = formMethods;

  const debouncedUrlUnique = useDebouncedUrlUnique();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'headers',
    rules: {
      validate: (headers) => {
        const complete = headers.filter((h) => _isNotBlank(h.key) && _isNotBlank(h.value));
        const empty = headers.filter((h) => !_isNotBlank(h.key) && !_isNotBlank(h.value));
        const partial = headers.filter((h) => _isNotBlank(h.key) !== _isNotBlank(h.value));
        if (complete.length === 0) {
          return 'At least one complete header is required';
        }
        if (partial.length > 0) {
          return 'Headers must have both a key and a value';
        }
        if (empty.length > 0 && headers.length > 1) {
          return 'Remove empty header rows or fill them in';
        }
        return true;
      },
    },
  });

  const {
    field: { value: events },
  } = useController({
    control,
    name: 'events',
    rules: {
      validate: (value) => (value?.length ?? 0) > 0 || 'At least one event is required',
    },
  });

  const isSubmitDisabled = !formState.isValid || formState.isValidating || isSubmitting;

  const { webhookEventSections, handleSetSelected, handleOnSelect, handleSelectAll } = useWebhookEvents(
    eventTypes,
    events,
    setValue
  );

  const registerHeader = useRegisterHeader(register, trigger);

  const handleAddHeader = useCallback(() => {
    append({ key: '', value: '' });
  }, [append]);

  const onSubmit = useCallback(
    async (formData: WebhookFormValues) => {
      try {
        const headers = formData.headers.reduce<Record<string, string>>((acc, { key, value }) => {
          if (_isNotBlank(key) && _isNotBlank(value)) {
            acc[key.trim()] = value.trim();
          }
          return acc;
        }, {});

        const body: WebhookCreate = {
          name: formData.name.trim(),
          url: formData.url,
          events: formData.events as WebhookCreate['events'],
          headers,
        };
        await createWebhook({ body } as Record<string, unknown>);
        onAddWebhook();
        closeModal();
        successToast('Webhook created');
      } catch {
        // Error already handled by useApiHook (toast + exception capture)
      }
    },
    [createWebhook, onAddWebhook, closeModal]
  );

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title="Add webhook"
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText={
          <>
            Configure a webhook endpoint to receive event notifications from your In Vivo tenant. See the{' '}
            <a
              href={RouteService.help('developer_platform')}
              target="_blank"
              rel="noopener noreferrer"
              className="link blue"
              data-testid="api-docs-link"
            >
              API docs
            </a>{' '}
            for details.
          </>
        }
        subTextClasses="f6 lh-copy mt2 measure-wide"
      />
      <FormProvider {...formMethods}>
        <div className={`pv3 ph2 overflow-y-auto ${styles.modalBody}`}>
          <div className={`mb3 ph2 ${styles.formInput}`}>
            <FormField name="name" label="Name" required testPrefix="webhook-name">
              <FormInput
                {...register('name', nameValidator)}
                name="name"
                type="text"
                placeholder="e.g. Production Event Notifications"
                maxLength={255}
                data-testid="webhook-name-input"
              />
            </FormField>
          </div>

          <div className={`mb3 ph2 ${styles.formInput}`}>
            <FormField name="url" label="HTTPS endpoint" required testPrefix="webhook-url">
              <div className={styles.urlRow}>
                <FormInput
                  {...register('url', {
                    required: 'URL is required',
                    validate: {
                      format: urlValidator,
                      unique: debouncedUrlUnique,
                    },
                  })}
                  name="url"
                  type="url"
                  placeholder="https://your-server.com/webhook"
                  maxLength={URL_MAX_LENGTH}
                  data-testid="webhook-url-input"
                />
                <div className={styles.headerSpacer} />
              </div>
            </FormField>
          </div>

          <div className="mb3 ph2">
            <FormField name="headers" label="Authentication headers" required testPrefix="webhook-headers">
              <div className={`mb1 ${styles.headerLabels}`}>
                <div className={styles.headerLabel}>
                  <label className="f7 dark-gray db">Header key</label>
                </div>
                <div className={styles.headerLabel}>
                  <label className="f7 dark-gray db">Header value</label>
                </div>
                <div className={styles.headerSpacer} />
              </div>
              {fields.map((field, index) => (
                <div key={field.id} className={`mb2 ${styles.headerRow}`} data-testid={`header-row-${index}`}>
                  <div className={styles.headerField}>
                    <FormInput
                      {...registerHeader(`headers.${index}.key`, headerKeyRules(index, getValues))}
                      name={`headers.${index}.key`}
                      type="text"
                      placeholder="X-API-Key"
                      data-testid={`header-key-${index}`}
                    />
                  </div>
                  <div className={styles.headerField}>
                    <FormInput
                      {...registerHeader(`headers.${index}.value`, headerValueRules(index))}
                      name={`headers.${index}.value`}
                      type="password"
                      placeholder="Value"
                      data-testid={`header-value-${index}`}
                    />
                  </div>
                  {index > 0 ? (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className={styles.removeHeader}
                      data-testid={`remove-header-${index}`}
                      aria-label={`Remove header ${index + 1}`}
                    >
                      <Icon icon="close" width="10" height="10" className="dark-gray" />
                    </button>
                  ) : (
                    <div className={styles.headerSpacer} />
                  )}
                </div>
              ))}
              <Button
                onClick={handleAddHeader}
                className="br-pill f6"
                icon="add_new"
                paleBlue
                testId="add-header-button"
              >
                <span>Add header</span>
              </Button>
              <FieldArrayError name="headers" />
            </FormField>
          </div>

          <div className="mb3 ph2">
            <FormField name="events" label="Events" required testPrefix="webhook-events">
              <div className={styles.searchSelectContainer}>
                <SearchSelect
                  sections={webhookEventSections}
                  selected={events ?? []}
                  setSelected={handleSetSelected}
                  search={true}
                  enableSelectAll={true}
                  customSearchPlaceholderText="Search events"
                  testPrefix="webhook-events"
                  overflowHeight={240}
                  onSelect={handleOnSelect}
                  onSelectAll={handleSelectAll}
                />
              </div>
            </FormField>
          </div>
        </div>
      </FormProvider>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitBtnText="Add webhook"
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: isSubmitDisabled,
          loading: isSubmitting,
        }}
      />
    </ModalContainer>
  );
};
